import type { ChangeEvent, ReactNode } from "react"
import { useController } from "react-hook-form"
import type {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
} from "react-hook-form"
import useField from "./useField"

type Props<TValues extends FieldValues, TName extends FieldPath<TValues>> = {
  name: TName
  required?: boolean
  // Opt out of the default "" → null coercion. Use for fields whose backend
  // schema requires a string and rejects null (e.g. prompt run inputs).
  emptyAsString?: boolean
  children: (props: {
    isError: boolean
    error: string | undefined
    field: ControllerRenderProps<TValues, TName>
  }) => ReactNode
}

export default function FormControl<
  TValues extends FieldValues,
  TName extends FieldPath<TValues>,
>({ name, required, emptyAsString, children }: Props<TValues, TName>) {
  const { control, error, isError } = useField<TValues>(name)
  const { field } = useController<TValues, TName>({
    name,
    control,
    rules: { required },
  })

  const { value, onChange, ...restField } = field

  const coerceEmpty = (val: unknown) =>
    !emptyAsString && val === "" ? null : val

  const nullSafeField = {
    ...restField,
    value: value ?? "",
    onChange: (e: unknown) => {
      if (
        e instanceof Event ||
        (typeof e === "object" && e !== null && "target" in e)
      ) {
        const val = (e as ChangeEvent<HTMLInputElement>).target.value
        onChange(coerceEmpty(val))
      } else {
        onChange(coerceEmpty(e))
      }
    },
  } as typeof field

  return <>{children({ field: nullSafeField, isError, error })}</>
}
